// src/utils/pastTripsAlbumTemplate.js

// 1. Import all the page components
import Cover from '../components/AlbumPageTemplates/Cover';
import Grid from '../components/AlbumPageTemplates/Grid';
import FullBleed from '../components/AlbumPageTemplates/FullBleed';
import Journal from '../components/AlbumPageTemplates/Journal';
import Timeline from '../components/AlbumPageTemplates/Timeline';
import StepIntro from '../components/AlbumPageTemplates/StepIntro';
import { StatsCard, MapCard } from '../album/SpecialPages';
// ADD THESE WITH YOUR OTHER COMPONENT IMPORTS
import JournalShort from '../components/AlbumPageTemplates/JournalShort';

export const DEFAULT_TEMPLATE_KEY = 'grid-4-square';

// REPLACE your entire TEMPLATES object with this corrected version:
export const TEMPLATES = {
  // --- LOCKED / SPECIAL TEMPLATES (Not user-selectable) ---
  'cover': {
    key: 'cover', name: 'Cover', component: Cover, slots: 1, isSpecial: true,
  },
  'stats': {
    key: 'stats', name: 'Statistics', component: StatsCard, slots: 0, isSpecial: true,
  },
  'map': {
    key: 'map', name: 'Route Map', component: MapCard, slots: 0, isSpecial: true,
  },
  'timeline': { key: 'timeline', name: 'Timeline', component: Timeline, slots: 0, isSpecial: true },

  'step-intro-left': {
    key: 'step-intro-left', name: 'Step Intro', component: StepIntro, slots: 1, isSpecial: true,
  },

  // --- USER-SELECTABLE TEMPLATES ---
  'hero-right': {
    key: 'hero-right', name: 'Full Page Photo', component: FullBleed, slots: 1,
  },
  'grid-2-tall': {
    key: 'grid-2-tall', name: '2 Photos (Tall)', component: Grid, slots: 2, 
    props: { cols: 1, rows: 2 }, 
  },
  'grid-3-wide': {
    key: 'grid-3-wide', name: '3 Photos (Wide)', component: Grid, slots: 3,
    props: { cols: 3, rows: 1 },
  },
  'grid-4-square': {
    key: 'grid-4-square', name: '4 Photos (Square)', component: Grid, slots: 4, 
    props: { cols: 2, rows: 2 },
  },
  'collage-hero-left': {
    key: 'collage-hero-left', name: 'Collage (1 Main + 3 Small)', component: Grid,
    slots: 4,
    props: { 
      areas: "'hero hero right1' 'hero hero right2' 'hero hero right3'",
      rows: 3,
      cols: 3
    },
  },
  'collage-hero-left-tall': {
    key: 'collage-hero-left-tall',
    name: 'Collage (1 Tall, 2 Small)',
    component: Grid,
    slots: 3,
    props: { 
      areas: "'hero right1' 'hero right2'",
      cols: 2,
      rows: 2
    },
  },
  'collage-hero-top-wide': {
    key: 'collage-hero-top-wide',
    name: 'Collage (1 Wide, 2 Square)',
    component: Grid,
    slots: 3,
    props: {
      areas: "'hero hero' 'bottom1 bottom2'",
      cols: 2,
      rows: 2
    },
  },
  'journal-right': {
    key: 'journal-right', name: 'Journal', component: Journal, slots: 1,
  },
  'journal-short': {
    key: 'journal-short',
    name: 'Journal (Short)',
    component: JournalShort,
    slots: 1,
  },
  
  
};

// This helper now correctly populates the editor dropdown with your desired list
export const getTemplateOptions = () => {
  return Object.values(TEMPLATES)
    .filter(t => {
      // Allow timeline to be selectable (so users can re-select it)
      // But keep other special pages (cover, stats, map, step-intro) locked
      if (t.key === 'timeline') return true;
      return !t.isSpecial;
    })
    .map(({ key, name }) => ({ key, name }));
};

// Other helpers remain the same
export function templateFor(key) { return TEMPLATES[key] || TEMPLATES[DEFAULT_TEMPLATE_KEY]; }
export function photosPerPageFor(key) { return templateFor(key).slots || 0; }
export function templateKeys() { return Object.keys(TEMPLATES); }
// ADD THIS CODE TO THE BOTTOM OF pastTripsAlbumTemplate.js

// Palettes for the smart pacing algorithm
export const ELEGANT_LEFT_PAGES = ['grid-4-square', 'collage-hero-left-tall', 'grid-2-tall'];
export const DENSE_PAGES = ['grid-4-square', 'collage-hero-top-wide', 'grid-3-wide', 'collage-hero-left-tall'];

// --- SANITIZER for captions (shared across templates) ---
export function sanitizeRichText(html) {
  if (typeof html !== 'string') return '';
  try {
    const div = document.createElement('div');
    div.innerHTML = html;

    // Allow formatting tags including span and font for colors/sizes
    const ALLOWED = new Set(['b','strong','i','em','u','br','p','h1','h2','h3','ul','ol','li','mark','span','font']);
    
    // Safe attributes for styling
    const SAFE_ATTRS = new Set(['color', 'size', 'style', 'class']);
    
    const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT, null);
    const toRemove = [];
    
    while (walker.nextNode()) {
      const el = walker.currentNode;
      const tag = el.tagName ? el.tagName.toLowerCase() : '';
      
      if (!ALLOWED.has(tag)) {
        // Remove disallowed tags but keep their content
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        toRemove.push(el);
      } else {
        // Clean attributes - only keep safe ones
        [...el.attributes].forEach(attr => {
          const n = attr.name.toLowerCase();
          
          // Remove dangerous attributes (onclick, onerror, etc.)
          if (n.startsWith('on')) {
            el.removeAttribute(attr.name);
            return;
          }
          
          // For style attribute, sanitize the CSS
          if (n === 'style') {
            const style = attr.value;
            // Only allow safe CSS properties
            const safeCss = style
              .split(';')
              .filter(prop => {
                const cleaned = prop.trim().toLowerCase();
                return cleaned.startsWith('color:') || 
                       cleaned.startsWith('font-size:') ||
                       cleaned.startsWith('background:') ||
                       cleaned.startsWith('background-color:');
              })
              .join(';');
            
            if (safeCss) {
              el.setAttribute('style', safeCss);
            } else {
              el.removeAttribute('style');
            }
            return;
          }
          
          // Keep safe attributes
          if (!SAFE_ATTRS.has(n)) {
            el.removeAttribute(attr.name);
          }
        });
      }
    }
    
    toRemove.forEach(n => n.parentNode && n.parentNode.removeChild(n));
    return div.innerHTML;
  } catch {
    return '';
  }
}